import React from "react";
import { observer } from "mobx-react";
import { useStore } from "utils/useStore";
import { AuthStore } from "stores/AuthStore";
import AcxBetaTag from "./AcxBetaTag";
import AcxDeprecatedTag from "./AcxDeprecatedTag";
import AcxNewTag from "./AcxNewTag";

export { AcxBetaTag, AcxDeprecatedTag, AcxNewTag };

/**
 * The statuses a surface can advertise. Carried as data on filter items, group-by options, and
 * endpoint behavior config so the same value drives every place the surface is named.
 */
export type StatusTag = "New" | "Deprecated" | "Beta";

/**
 * A status and the optional hover copy explaining it. One shape for every kind of surface that
 * advertises a status — Signals Cards declare it on their endpoint behavior config, filters on
 * their filter item — so the two can never describe the same idea differently.
 *
 * Spreads straight into {@link StatusTagChip}: `<StatusTagChip {...status} />`.
 */
export type StatusDescriptor = {
    tag: StatusTag;
    infoText?: string;
    /**
     * V2 resource the viewer must be able to View before the tag renders. Omitted — the default —
     * means the tag always shows.
     *
     * This gates the *tag*, never the surface it sits on. A Card or filter stays exactly as visible
     * and as usable as it was; only the marker explaining its status disappears. Entitlement to the
     * surface itself is decided elsewhere, by route authorization and `filterByPerms`.
     */
    permission?: string;
};

/**
 * Whether a status should render for the current viewer.
 *
 * No status, no tag. No declared permission, always show. Otherwise the viewer needs View on the
 * declared resource.
 *
 * Exported because a few call sites need the answer *before* they render, not just at the chip —
 * the Card headers wrap their tag in a spaced `Grid` item, and an unconditional wrapper around a
 * chip that renders nothing would leave a stray gap in the header.
 */
export const isStatusTagVisible = (
    status: StatusDescriptor | undefined,
    canView: (resource: string) => boolean,
): boolean => {
    if (!status?.tag) return false;
    if (!status.permission) return true;
    return canView(status.permission);
};

export const useStatusTagVisible = (status?: StatusDescriptor): boolean => {
    const authStore = useStore(AuthStore);
    return isStatusTagVisible(status, (resource) =>
        authStore.canUserView(resource),
    );
};

/**
 * Whether a surface should be presented as legacy: it advertises Deprecated *and* this viewer can
 * see that it does.
 *
 * The permission half is the point. A tenant without the replacement has no transition to be in, so
 * showing them a Legacy section — collecting filters whose Deprecated tags they cannot see — would
 * label things legacy while withholding the reason. Those surfaces stay in their ordinary
 * categories instead, exactly as before this release.
 *
 * Deliberately keyed on the same rule the tag itself uses, so a surface can never sit under a
 * Legacy heading without the tag that explains it, or carry the tag while filed as current.
 */
export const isLegacyStatus = (
    status: StatusDescriptor | undefined,
    canView: (resource: string) => boolean,
): boolean =>
    status?.tag === "Deprecated" && isStatusTagVisible(status, canView);

/** Hook form of {@link isLegacyStatus}, for components that already have hooks available. */
export const useIsLegacyStatus = (status?: StatusDescriptor): boolean => {
    const authStore = useStore(AuthStore);
    return isLegacyStatus(status, (resource) => authStore.canUserView(resource));
};

/**
 * Renders the tag for a status, or nothing when there isn't one. Call sites stay a single
 * expression instead of repeating the same three-way branch.
 *
 * `infoText` is the optional hover explanation, forwarded to whichever tag is rendered so a
 * caller passing one never has it silently dropped by the branch it happens to hit.
 *
 * `permission` gates the whole chip through {@link useStatusTagVisible}, so every site that spreads
 * a `StatusDescriptor` in gets the gate without opting into it.
 */
export const StatusTagChip = observer(
    ({ tag, infoText, permission }: Partial<StatusDescriptor>) => {
        const isVisible = useStatusTagVisible(
            tag ? { tag, infoText, permission } : undefined,
        );
        if (!isVisible) return null;
        if (tag === "Deprecated")
            return <AcxDeprecatedTag infoText={infoText} />;
        if (tag === "New") return <AcxNewTag infoText={infoText} />;
        if (tag === "Beta") return <AcxBetaTag infoText={infoText} />;
        return null;
    },
);
